import { Document, Paragraph, TextRun, Packer, HeadingLevel, UnderlineType } from 'docx'

function styledContainer(html: string): HTMLDivElement {
  const container = document.createElement('div')
  container.innerHTML = html
  container.style.fontFamily = 'Arial, sans-serif'
  container.style.fontSize = '12pt'
  container.style.lineHeight = '1.6'
  container.style.padding = '40px'
  container.style.maxWidth = '800px'
  container.style.margin = '0 auto'
  container.style.color = '#000'
  container.style.background = '#fff'

  container.querySelectorAll('h2').forEach((el) => {
    const h = el as HTMLElement
    h.style.fontSize = '18pt'
    h.style.fontWeight = 'bold'
    h.style.marginTop = '24px'
    h.style.marginBottom = '12px'
    h.style.color = '#000'
  })
  container.querySelectorAll('p').forEach((el) => {
    const p = el as HTMLElement
    p.style.marginBottom = '12px'
    p.style.color = '#000'
  })
  container.querySelectorAll('ul, ol').forEach((el) => {
    const list = el as HTMLElement
    list.style.marginBottom = '12px'
    list.style.paddingLeft = '24px'
  })
  container.querySelectorAll('li').forEach((el) => {
    const li = el as HTMLElement
    li.style.marginBottom = '4px'
  })
  container.querySelectorAll('a').forEach((el) => {
    const a = el as HTMLElement
    a.style.color = '#2563eb'
    a.style.textDecoration = 'underline'
  })
  container.querySelectorAll('p, li, h2').forEach((el) => {
    const el2 = el as HTMLElement
    el2.style.pageBreakInside = 'avoid'
    el2.style.breakInside = 'avoid'
  })

  return container
}

export async function exportDocumentPDF(html: string, filename = 'legal-document'): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe')
    iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:800px;height:1200px;border:none;'

    let timeoutId: ReturnType<typeof setTimeout>

    const cleanup = () => {
      clearTimeout(timeoutId)
      if (iframe.parentNode) {
        document.body.removeChild(iframe)
      }
      window.removeEventListener('message', handleMessage)
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return
      if (event.data?.type === 'pdf-done') {
        cleanup()
        resolve()
      } else if (event.data?.type === 'pdf-error') {
        cleanup()
        reject(new Error(event.data.error || 'PDF export failed'))
      }
    }

    window.addEventListener('message', handleMessage)
    timeoutId = setTimeout(() => {
      cleanup()
      reject(new Error('PDF export timed out'))
    }, 30000)

    document.body.appendChild(iframe)

    const iframeDoc = iframe.contentDocument
    if (!iframeDoc) {
      cleanup()
      reject(new Error('Failed to access iframe document'))
      return
    }

    const safeFilename = JSON.stringify(`${filename}.pdf`)

    const bundleScript = iframeDoc.createElement('script')
    bundleScript.src = '/vendor/html2pdf.bundle.min.js'
    bundleScript.onload = () => {
      const container = styledContainer(html)
      iframeDoc.body.innerHTML = ''
      iframeDoc.body.appendChild(container)

      const exportScript = iframeDoc.createElement('script')
      exportScript.textContent = `
        (async () => {
          try {
            await html2pdf().set({
              margin: [15, 15, 15, 15],
              filename: ${safeFilename},
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: { scale: 1, useCORS: true, logging: false },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
              pagebreak: { mode: ['css', 'legacy'], avoid: 'p, li, h2' }
            }).from(document.body).save();
            window.parent.postMessage({ type: 'pdf-done' }, '*');
          } catch (e) {
            window.parent.postMessage({ type: 'pdf-error', error: e.message }, '*');
          }
        })();
      `
      iframeDoc.body.appendChild(exportScript)
    }
    bundleScript.onerror = () => {
      cleanup()
      reject(new Error('Failed to load html2pdf.js bundle'))
    }

    iframeDoc.head.appendChild(bundleScript)
  })
}

export async function exportDocumentDOCX(html: string, filename = 'legal-document'): Promise<void> {
  const children: Array<Paragraph> = []
  const temp = document.createElement('div')
  temp.innerHTML = html

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim()
      if (text) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text, font: 'Arial', size: 24 })],
            spacing: { after: 120 },
          })
        )
      }
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()

    if (tag === 'h2') {
      children.push(
        new Paragraph({
          text: el.textContent || '',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 240, after: 120 },
        })
      )
    } else if (tag === 'p') {
      const runs: TextRun[] = []
      el.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const txt = child.textContent || ''
          if (txt) runs.push(new TextRun({ text: txt, font: 'Arial', size: 24 }))
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const c = child as HTMLElement
          const ct = c.textContent || ''
          if (!ct) return
          const cTag = c.tagName.toLowerCase()
          if (cTag === 'strong' || cTag === 'b') {
            runs.push(new TextRun({ text: ct, bold: true, font: 'Arial', size: 24 }))
          } else if (cTag === 'em' || cTag === 'i') {
            runs.push(new TextRun({ text: ct, italics: true, font: 'Arial', size: 24 }))
          } else if (cTag === 'u') {
            runs.push(new TextRun({ text: ct, underline: { type: UnderlineType.SINGLE }, font: 'Arial', size: 24 }))
          } else if (cTag === 'a') {
            runs.push(new TextRun({ text: ct, color: '2563EB', underline: { type: UnderlineType.SINGLE }, font: 'Arial', size: 24 }))
          } else {
            runs.push(new TextRun({ text: ct, font: 'Arial', size: 24 }))
          }
        }
      })
      if (runs.length > 0) {
        children.push(new Paragraph({ children: runs, spacing: { after: 120 } }))
      }
    } else if (tag === 'ul' || tag === 'ol') {
      el.querySelectorAll('li').forEach((li) => {
        children.push(
          new Paragraph({
            text: '\u2022 ' + (li.textContent || ''),
            spacing: { after: 80 },
            indent: { left: 720 },
          })
        )
      })
    } else if (tag !== 'li') {
      el.childNodes.forEach(walk)
    }
  }

  temp.childNodes.forEach(walk)

  if (children.length === 0) {
    const plainText = temp.textContent || ''
    if (plainText.trim()) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: plainText, font: 'Arial', size: 24 })],
        })
      )
    }
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
          },
        },
        children,
      },
    ],
  })

  const blob = await Packer.toBlob(doc)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.docx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
